import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useCallback,
} from "react";
import {
  AppState,
  CartItem,
  Cart,
  ShopifyProduct,
  Order,
  Property,
  Kiosk,
} from "@/types";

// Action types
type AppAction =
  | { type: "SET_PRODUCTS"; payload: ShopifyProduct[] }
  | {
      type: "UPDATE_CART_ITEM";
      payload: { productId: string; variantId: string; quantity: number };
    }
  | { type: "SET_CART"; payload: { cart: Cart; cartId: string | null } }
  | {
      type: "UPDATE_LOCAL_CART_ITEM";
      payload: {
        productId: string;
        variantId: string;
        quantity: number;
        sellingPlanId?: string;
      };
    }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_ORDER"; payload: Order | null }
  | { type: "SET_ORDER_CONFIRMED"; payload: boolean }
  | { type: "SET_TIMER_EXPIRED"; payload: boolean }
  | { type: "CLEAR_CART" }
  | { type: "CLEAR_LOCAL_CART" }
  | { type: "RESET_APP" }
  | { type: "CONFIRM_DONATION" }
  | { type: "OPEN_CONFIRMATION_SHEET" }
  | { type: "CLOSE_CONFIRMATION_SHEET" }
  | { type: "OPEN_HOW_IT_WORKS_MODAL" }
  | { type: "CLOSE_HOW_IT_WORKS_MODAL" }
  | { type: "OPEN_PROPERTY_KIOSK_SELECTION" }
  | { type: "CLOSE_PROPERTY_KIOSK_SELECTION" }
  | {
      type: "SET_SELECTED_PROPERTY_KIOSK";
      payload: { property: Property; kiosk: Kiosk };
    }
  | { type: "CLEAR_PROPERTY_KIOSK_SELECTION" }
  | { type: "SET_SESSION_ID"; payload: string | null }
  | { type: "SET_QR_SCANNED"; payload: boolean }
  | { type: "SHOW_CONFETTI"; payload: boolean }
  | { type: "RESET_PRODUCT_DROPDOWNS" }
  | { type: "CLEAR_PRODUCT_DROPDOWNS_RESET" }
  | { type: "REFRESH_PRODUCTS" };

// Helper function to load property/kiosk from localStorage
const loadPropertyKioskFromStorage = () => {
  try {
    const stored = localStorage.getItem("selectedPropertyKiosk");
    if (stored) {
      const { property, kiosk } = JSON.parse(stored);
      return { property, kiosk };
    }
  } catch (error) {
    console.error("Error loading property/kiosk from localStorage:", error);
  }
  return { property: null, kiosk: null };
};

// Helper function to save property/kiosk to localStorage
const savePropertyKioskToStorage = (property: Property, kiosk: Kiosk) => {
  try {
    localStorage.setItem(
      "selectedPropertyKiosk",
      JSON.stringify({ property, kiosk })
    );
  } catch (error) {
    console.error("Error saving property/kiosk to localStorage:", error);
  }
};

// Initial state
const initialCart = {
  items: [],
  totalQuantity: 0,
  totalPrice: 0,
};

const { property: storedProperty, kiosk: storedKiosk } =
  loadPropertyKioskFromStorage();

// Check for shareable link params: ?property=<id>&kiosk=<name>
const getShareableLinkParams = () => {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const propertyId = params.get("property");
  const kiosk = params.get("kiosk");
  if (propertyId && kiosk) {
    return { propertyId, kiosk };
  }
  return null;
};

const shareableLinkParams = getShareableLinkParams();

const initialState: AppState = {
  products: [],
  cart: initialCart,
  cartId: null,
  localCart: initialCart,
  isLoading: false,
  error: null,
  order: null,
  isOrderConfirmed: false,
  timerExpired: false,
  isConfirmationSheetOpen: false,
  isHowItWorksModalOpen: false,
  isPropertyKioskSelectionOpen: shareableLinkParams
    ? false
    : !storedProperty || !storedKiosk,
  selectedProperty: shareableLinkParams ? null : storedProperty,
  selectedKiosk: shareableLinkParams ? null : storedKiosk,
  sessionId: null,
  isQRScanned: false,
  showConfetti: false,
  resetProductDropdowns: false,
};

// Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case "SET_PRODUCTS":
      return { ...state, products: action.payload };

    case "SET_CART":
      return {
        ...state,
        cart: action.payload.cart,
        cartId: action.payload.cartId,
      };

    case "UPDATE_CART_ITEM": {
      const { productId, variantId, quantity } = action.payload;
      const product = state.products.find((p) => p.id === productId);

      if (!product) return state;

      const variant = product.variants.edges.find(
        (v) => v.node.id === variantId
      )?.node;

      if (!variant) return state;

      const existingItemIndex = state.cart.items.findIndex(
        (item) => item.productId === productId && item.variantId === variantId
      );

      let newItems = [...state.cart.items];

      if (quantity === 0) {
        // Remove item from cart
        newItems = newItems.filter(
          (item) =>
            !(item.productId === productId && item.variantId === variantId)
        );
      } else if (existingItemIndex >= 0) {
        // Update existing item
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity,
          price: parseFloat(variant.price.amount),
        };
      } else {
        // Add new item
        const newItem: CartItem = {
          productId,
          variantId,
          title: product.title,
          variantTitle: variant.title,
          price: parseFloat(variant.price.amount),
          quantity,
          image: product.images.edges[0]?.node.url,
        };
        newItems.push(newItem);
      }

      // Calculate totals
      const totalQuantity = newItems.reduce(
        (sum, item) => sum + item.quantity,
        0
      );
      const totalPrice = newItems.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );

      return {
        ...state,
        cart: {
          items: newItems,
          totalQuantity,
          totalPrice,
        },
      };
    }

    case "SET_LOADING":
      return { ...state, isLoading: action.payload };

    case "SET_ERROR":
      return { ...state, error: action.payload };

    case "SET_ORDER":
      return { ...state, order: action.payload };

    case "SET_ORDER_CONFIRMED":
      return { ...state, isOrderConfirmed: action.payload };

    case "SET_TIMER_EXPIRED":
      return { ...state, timerExpired: action.payload };

    case "CLEAR_CART":
      return { ...state, cart: initialCart };

    case "RESET_APP":
      return {
        ...initialState,
        products: state.products, // Keep products loaded
        cartId: null, // Reset cart ID to create new cart
      };

    case "OPEN_CONFIRMATION_SHEET":
      return { ...state, isConfirmationSheetOpen: true };

    case "CLOSE_CONFIRMATION_SHEET":
      return { ...state, isConfirmationSheetOpen: false };

    case "OPEN_HOW_IT_WORKS_MODAL":
      return { ...state, isHowItWorksModalOpen: true };

    case "CLOSE_HOW_IT_WORKS_MODAL":
      return { ...state, isHowItWorksModalOpen: false };

    case "UPDATE_LOCAL_CART_ITEM": {
      const { productId, variantId, quantity, sellingPlanId } = action.payload;
      const product = state.products.find((p) => p.id === productId);

      if (!product) return state;

      const variant = product.variants.edges.find(
        (v) => v.node.id === variantId
      )?.node;

      if (!variant) return state;

      const existingItemIndex = state.localCart.items.findIndex(
        (item) => item.productId === productId && item.variantId === variantId
      );

      let newItems = [...state.localCart.items];

      if (quantity === 0) {
        // Remove item from cart
        newItems = newItems.filter(
          (item) =>
            !(item.productId === productId && item.variantId === variantId)
        );
      } else if (existingItemIndex >= 0) {
        // Update existing item
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity,
          price: parseFloat(variant.price.amount),
          sellingPlanId,
        };
      } else {
        // Add new item
        const newItem: CartItem = {
          productId,
          variantId,
          title: product.title,
          variantTitle: variant.title,
          quantity,
          price: parseFloat(variant.price.amount),
          image: product.images.edges[0]?.node.url || "",
          sellingPlanId,
        };
        newItems.push(newItem);
      }

      const totalQuantity = newItems.reduce(
        (sum, item) => sum + item.quantity,
        0
      );
      const totalPrice = newItems.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );

      return {
        ...state,
        localCart: {
          items: newItems,
          totalQuantity,
          totalPrice,
        },
      };
    }

    case "CLEAR_LOCAL_CART":
      return {
        ...state,
        localCart: initialCart,
      };

    case "OPEN_PROPERTY_KIOSK_SELECTION":
      return { ...state, isPropertyKioskSelectionOpen: true };

    case "CLOSE_PROPERTY_KIOSK_SELECTION":
      return { ...state, isPropertyKioskSelectionOpen: false };

    case "SET_SELECTED_PROPERTY_KIOSK": {
      const { property, kiosk } = action.payload;
      // Save to localStorage
      savePropertyKioskToStorage(property, kiosk);
      return {
        ...state,
        selectedProperty: property,
        selectedKiosk: kiosk,
        isPropertyKioskSelectionOpen: false,
      };
    }

    case "CLEAR_PROPERTY_KIOSK_SELECTION": {
      // Clear from localStorage
      localStorage.removeItem("selectedPropertyKiosk");
      return {
        ...state,
        selectedProperty: null,
        selectedKiosk: null,
        isPropertyKioskSelectionOpen: true,
      };
    }

    case "SET_SESSION_ID":
      return { ...state, sessionId: action.payload };

    case "SET_QR_SCANNED":
      return { ...state, isQRScanned: action.payload };

    case "SHOW_CONFETTI":
      return { ...state, showConfetti: action.payload };

    case "RESET_PRODUCT_DROPDOWNS":
      return { ...state, resetProductDropdowns: true };

    case "CLEAR_PRODUCT_DROPDOWNS_RESET":
      return { ...state, resetProductDropdowns: false };

    case "REFRESH_PRODUCTS":
      // Clear products to trigger reload in App.tsx
      return { ...state, products: [] };

    default:
      return state;
  }
}

// Context
const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | null>(null);

// Provider component
export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

// Custom hook to use the context
export function useAppStore() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppStore must be used within an AppProvider");
  }

  const { state, dispatch } = context;

  // Action creators with useCallback to prevent infinite re-renders
  const setProducts = useCallback(
    (products: ShopifyProduct[]) =>
      dispatch({ type: "SET_PRODUCTS", payload: products }),
    []
  );

  const updateCartItem = useCallback(
    (productId: string, variantId: string, quantity: number) =>
      dispatch({
        type: "UPDATE_CART_ITEM",
        payload: { productId, variantId, quantity },
      }),
    []
  );

  const setCart = useCallback(
    (cart: Cart, cartId: string | null) =>
      dispatch({
        type: "SET_CART",
        payload: { cart, cartId },
      }),
    []
  );

  const setLoading = useCallback(
    (isLoading: boolean) =>
      dispatch({ type: "SET_LOADING", payload: isLoading }),
    []
  );

  const setError = useCallback(
    (error: string | null) => dispatch({ type: "SET_ERROR", payload: error }),
    []
  );

  const setOrder = useCallback(
    (order: Order | null) => dispatch({ type: "SET_ORDER", payload: order }),
    []
  );

  const setOrderConfirmed = useCallback(
    (isOrderConfirmed: boolean) =>
      dispatch({ type: "SET_ORDER_CONFIRMED", payload: isOrderConfirmed }),
    []
  );

  const setTimerExpired = useCallback(
    (timerExpired: boolean) =>
      dispatch({ type: "SET_TIMER_EXPIRED", payload: timerExpired }),
    []
  );

  const clearCart = useCallback(() => dispatch({ type: "CLEAR_CART" }), []);

  const resetApp = useCallback(() => dispatch({ type: "RESET_APP" }), []);

  const confirmDonation = useCallback(
    () => dispatch({ type: "CONFIRM_DONATION" }),
    []
  );

  const openConfirmationSheet = useCallback(
    () => dispatch({ type: "OPEN_CONFIRMATION_SHEET" }),
    []
  );

  const closeConfirmationSheet = useCallback(
    () => dispatch({ type: "CLOSE_CONFIRMATION_SHEET" }),
    []
  );

  const openHowItWorksModal = useCallback(
    () => dispatch({ type: "OPEN_HOW_IT_WORKS_MODAL" }),
    []
  );

  const closeHowItWorksModal = useCallback(
    () => dispatch({ type: "CLOSE_HOW_IT_WORKS_MODAL" }),
    []
  );

  const updateLocalCartItem = useCallback(
    (
      productId: string,
      variantId: string,
      quantity: number,
      sellingPlanId?: string
    ) =>
      dispatch({
        type: "UPDATE_LOCAL_CART_ITEM",
        payload: { productId, variantId, quantity, sellingPlanId },
      }),
    []
  );

  const clearLocalCart = useCallback(
    () => dispatch({ type: "CLEAR_LOCAL_CART" }),
    []
  );

  const openPropertyKioskSelection = useCallback(
    () => dispatch({ type: "OPEN_PROPERTY_KIOSK_SELECTION" }),
    []
  );

  const closePropertyKioskSelection = useCallback(
    () => dispatch({ type: "CLOSE_PROPERTY_KIOSK_SELECTION" }),
    []
  );

  const setSelectedPropertyKiosk = useCallback(
    (property: Property, kiosk: Kiosk) =>
      dispatch({
        type: "SET_SELECTED_PROPERTY_KIOSK",
        payload: { property, kiosk },
      }),
    []
  );

  const clearPropertyKioskSelection = useCallback(
    () => dispatch({ type: "CLEAR_PROPERTY_KIOSK_SELECTION" }),
    []
  );

  const setSessionId = useCallback(
    (sessionId: string | null) =>
      dispatch({ type: "SET_SESSION_ID", payload: sessionId }),
    []
  );

  const setQRScanned = useCallback(
    (isQRScanned: boolean) =>
      dispatch({ type: "SET_QR_SCANNED", payload: isQRScanned }),
    []
  );

  const setShowConfetti = useCallback(
    (showConfetti: boolean) =>
      dispatch({ type: "SHOW_CONFETTI", payload: showConfetti }),
    []
  );

  const triggerResetProductDropdowns = useCallback(
    () => dispatch({ type: "RESET_PRODUCT_DROPDOWNS" }),
    []
  );

  const clearProductDropdownsReset = useCallback(
    () => dispatch({ type: "CLEAR_PRODUCT_DROPDOWNS_RESET" }),
    []
  );

  const refreshProducts = useCallback(
    () => dispatch({ type: "REFRESH_PRODUCTS" }),
    []
  );

  return {
    // State
    ...state,
    // Actions
    setProducts,
    updateCartItem,
    setCart,
    setLoading,
    setError,
    setOrder,
    setOrderConfirmed,
    setTimerExpired,
    clearCart,
    resetApp,
    confirmDonation,
    openConfirmationSheet,
    closeConfirmationSheet,
    openHowItWorksModal,
    closeHowItWorksModal,
    updateLocalCartItem,
    clearLocalCart,
    openPropertyKioskSelection,
    closePropertyKioskSelection,
    setSelectedPropertyKiosk,
    clearPropertyKioskSelection,
    setSessionId,
    setQRScanned,
    setShowConfetti,
    triggerResetProductDropdowns,
    clearProductDropdownsReset,
    refreshProducts,
  };
}
